import { useCallback, useEffect, useRef, useState } from "react";
import { NameBasisBadge } from "../components/NameBasisBadge";
import { Link, useSearchParams } from "react-router-dom";
import { api, apiBase, type RegisteredIssuer } from "../api/client";
import { applyEvent, applySnapshot, empty, focus, TYPE_LABEL, type GraphState, type NodeType, type Standing } from "../live/model";
import { follow, eventsUrl } from "../live/stream";
import { badgeFor, type Conn } from "../live/badge";
import { InsightsPanel } from "../insights/InsightsPanel";
import { LiveRenderer, viewOf } from "../live/render";
import { shortDid } from "../util";
import { DidLabel } from "../components/DidLabel";

const RUNG_LABEL: Record<string, string> = { self_claimed: "Self-claimed", peer_vouched: "Peer-vouched", affiliated: "Affiliated",
  organisation_confirmed: "Confirmed by an organisation", anchored: "Anchored" };
const STANDING_LABEL: Record<Standing, string> = { new: "New", established: "Established", strong: "Strong", compromised: "Key compromised" };

/** /live: the whole rail as a force graph, following the live stream. For a projector;
 *  ?mode=replay assembles from a replay, ?did=<did> focuses on one DID's fan. */
export function LivePage() {
  const [params, setParams] = useSearchParams();
  const replay = params.get("mode") === "replay";
  const focusDid = params.get("did") ?? "";
  const svgRef = useRef<SVGSVGElement>(null);
  const renderer = useRef<LiveRenderer | null>(null);
  const graph = useRef<GraphState>(empty(replay ? "replay" : "live"));
  // The raw connection state, and when it began; the badge is derived (badge.ts), silent through short drops.
  const [conn, setConn] = useState<Conn>({ s: "connecting", since: Date.now(), wasLive: false });
  const [now, setNow] = useState(Date.now());
  const [replaying, setReplaying] = useState(false);
  const [counts, setCounts] = useState<{ claims: number; vouches: number; anchors: number } | null>(null);
  const [selected, setSelected] = useState<string>("");
  const [profile, setProfile] = useState<any>(null);
  const [filter, setFilter] = useState(focusDid);
  const lastReplay = useRef(0);

  // The focused DID lives in a ref, so changing it never rebuilds the renderer or the stream.
  const focusRef = useRef(focusDid);
  const draw = useCallback(() => {
    const g = graph.current, did = focusRef.current;
    renderer.current?.update(did ? focus(g, did) : viewOf(g), g);
  }, []);

  useEffect(() => {
    const svg = svgRef.current!;
    const r = new LiveRenderer(svg, (did) => setSelected(did));
    renderer.current = r;
    const resize = () => r.resize(svg.clientWidth || window.innerWidth, svg.clientHeight || window.innerHeight);
    resize(); window.addEventListener("resize", resize);
    graph.current = empty(replay ? "replay" : "live");
    const stop = follow({ url: eventsUrl(apiBase), snapshot: async () => {
        const res = await api.GET("/rail/graph");
        if (!res.data) throw new Error("no graph");
        return res.data;
      } },
      { onSnapshot: (s) => { graph.current = applySnapshot(graph.current, s); draw(); },
        onEvent: (e) => {
          graph.current = applyEvent(graph.current, e, Date.now());
          if (e.replay) { lastReplay.current = Date.now(); setReplaying(true); }
          draw();
        },
        onStatus: (s) => setConn((c) => {
          const live = s === "live", wasNotLive = c.s !== "live";
          // A failure period starts when the stream stops being live and lasts through every retry.
          if (live) return { s, since: Date.now(), wasLive: true };
          return { s, since: wasNotLive ? c.since : Date.now(), wasLive: c.wasLive };
        }) }, { replay });
    // Pulses fade and replays end without a message: redraw on a slow clock.
    const t = setInterval(() => { if (Date.now() - lastReplay.current > 5000) setReplaying(false); setNow(Date.now()); draw(); }, 1000);
    return () => { stop(); clearInterval(t); window.removeEventListener("resize", resize); r.stop(); };
  }, [replay, draw]);

  useEffect(() => {
    const load = () => api.GET("/rail/counts").then((r) => r.data && setCounts(r.data));
    load(); const t = setInterval(load, 10_000); return () => clearInterval(t);
  }, []);

  // While a profile is open the layout holds still (stream events are drawn in place).
  useEffect(() => { renderer.current?.setHeld(!!selected); }, [selected]);
  // Enter or leave a DID's fan: the ego-graph alone (laid out and fitted), or the full graph restored.
  useEffect(() => {
    focusRef.current = focusDid;
    renderer.current?.setFocus(focusDid || null);
    draw();
    setFilter(focusDid);
  }, [focusDid, draw]);
  const leaveFocus = useCallback(() => setParams(replay ? { mode: "replay" } : {}), [replay, setParams]);
  useEffect(() => {
    if (!focusDid) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") leaveFocus(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusDid, leaveFocus]);

  // An organisation's registry entry (name, domain), fetched on tap.
  const [issuer, setIssuer] = useState<RegisteredIssuer | null>(null);
  useEffect(() => {
    setIssuer(null);
    if (!selected) return;
    api.GET("/registry/issuers").then((r) => setIssuer((r.data?.issuers ?? []).find((i) => i.did === selected) ?? null));
  }, [selected]);

  useEffect(() => {
    if (!selected) { setProfile(null); return; }
    api.GET("/dids/{did}/profile", { params: { path: { did: selected } } }).then((r) => setProfile(r.data ?? null));
  }, [selected]);

  const badge = badgeFor(conn, now);
  const statusText = replaying ? "Replaying the last week" : badge.text;
  const shownStatus = replaying ? "replay" : badge.status;

  return (
    <div className="live" data-testid="live" data-conn={conn.s}>
      <div className="live-stage">
      <svg ref={svgRef} className="live-svg" role="img" aria-label="The rail's live graph" data-testid="live-svg" />
      <div className="live-top">
        <Link to="/" className="brand">QSeverse <span>Live</span></Link>
        <span className={`live-status s-${shownStatus}`} data-testid="live-status" data-status={shownStatus} hidden={!statusText}>{statusText}</span>
      </div>
      {counts && (
        <div className="live-counts" data-testid="live-counts">
          <span><b>{counts.claims.toLocaleString()}</b> claims</span><span><b>{counts.vouches.toLocaleString()}</b> vouches</span>
          <span><b>{counts.anchors.toLocaleString()}</b> anchors</span>
        </div>
      )}
      <form className="live-filter" onSubmit={(e) => { e.preventDefault(); setParams(filter ? { ...(replay ? { mode: "replay" } : {}), did: filter.trim() } : (replay ? { mode: "replay" } : {})); }}>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Focus on one DID" aria-label="Focus on one DID" data-testid="live-filter" />
        <button type="submit">{focusDid ? "Refocus" : "Focus"}</button>
        {focusDid && <button type="button" className="primary" onClick={leaveFocus} data-testid="live-back">Back to the full graph</button>}
      </form>
      <div className="live-legend" aria-hidden="true">
        {(["peer", "affiliated", "light_issuer", "issuer"] as NodeType[]).map((t) => <span key={t} className={`lg lg-${t}`}>{TYPE_LABEL[t]}</span>)}
        <span className="lg lg-anchor">◆ anchored claim</span>
        <span className="lg lg-delegation" data-testid="legend-delegation">┄ delegation (reviewer to issuer)</span>
      </div>
      {profile && (
        <aside className="live-profile card" data-testid="live-profile" aria-live="polite">
          <button className="close" onClick={() => setSelected("")} aria-label="Close">×</button>
          {(() => {
            // Names only here, fetched on tap (the graph carries none): a person's self-asserted name, always
            // labelled; an issuer's registry name with its name basis; a light issuer's domain with its proof.
            const n: any = graph.current.nodes.get(profile.did);
            const basis = profile.name_basis ?? n?.name_basis;
            const org = issuer?.did === profile.did ? issuer : undefined;
            if (profile.type === "issuer" || profile.type === "light_issuer")
              return <div data-testid="live-profile-name"><b>{org ? (org.kind === "light" ? org.domain ?? org.name : org.name) : shortDid(profile.did)}</b>{" "}
                <NameBasisBadge basis={basis} domain={org?.domain} /></div>;
            return <div data-testid="live-profile-name"><DidLabel did={profile.did} name={profile.display_name ?? null} /></div>;
          })()}
          <div className="mono hint">{shortDid(profile.did)}</div>
          <div><b>{TYPE_LABEL[profile.type as NodeType]}</b> · {STANDING_LABEL[profile.standing as Standing] ?? profile.standing}</div>
          {profile.rung && <div>Highest rung: {RUNG_LABEL[profile.rung]}</div>}
          <div>{profile.claims} claims{Object.entries(profile.claims_by_rung ?? {}).map(([r, n]) => ` · ${n} ${RUNG_LABEL[r]?.toLowerCase()}`).join("")}</div>
          <div>{profile.vouches_given} vouches given · {profile.anchors_given} anchors given</div>
          {profile.tags?.length > 0 && <div className="tags">{profile.tags.slice(0, 12).map((t: string) => <span className="tag" key={t}>{t}</span>)}</div>}
          <div className="row" style={{ marginTop: 8 }}>
            <button onClick={() => { setFilter(profile.did); setParams({ ...(replay ? { mode: "replay" } : {}), did: profile.did }); }} data-testid="live-focus">Show this DID's fan</button>
            <Link className="btn" to={`/d/${encodeURIComponent(profile.did)}`}>Chain</Link>
          </div>
        </aside>
      )}
      </div>
      <InsightsPanel replaying={replaying} focused={!!focusDid} onHighlight={(h) => renderer.current?.setHighlight(h)} />
    </div>
  );
}
